import clsx from 'clsx';
import type { ComponentPropsWithoutRef } from 'react';

import BaseContainer from '@commons/containers/BaseContainer';

import styles from './index.module.scss';

/**
 * Type definition for the types of tables.
 */

export type TableType = 'default' | 'border';

/**
 * Type definition for the Table component props.
 */

export type TableProps = ComponentPropsWithoutRef<'table'> & {
  type?: TableType;
};

/**
 * Table component.
 *
 * Components and styles can be altered based on the type prop.
 *
 * If it gets overwhelming, this component should be changed into a factory of table components.
 */

export const Table = ({ type = 'default', className, children, ...rest }: TableProps) => {
  const classes = clsx(styles.table, styles[`type-${type}`], className);

  return (
    <BaseContainer width={12} className={styles.tableWrapper}>
      <table className={classes} {...rest}>
        {children}
      </table>
    </BaseContainer>
  );
};

/**
 * Returns the table type based on the classes of the HTML table.
 *
 * Mostly used with the RichText component.
 *
 * @param classes - An array of strings representing the classes of the HTML table.
 *
 * @returns The table type.
 */

export const getTableTypeFromClasses = (classes: string[]): TableType => {
  const type = classes.find((className) => className.startsWith('table-'));

  switch (type) {
    case 'table-border':
      return 'border';
    default:
      return 'default';
  }
};

export default Table;
